define([
    '../Util/defaultValue',
    '../Util/defined',
    '../Util/encode',
    '../Enum/EnumOGCType'
], function (
    defaultValue,
    defined,
    encode,
    EnumOGCType
) {
    'use strict'

    /**
     * OGC服务连接器基类，用于连接和解析各种OGC标准服务
     * @alias OGCConnector
     * @constructor
     * @abstract
     */
    class OGCConnector {
        /**
         * @description 服务地址
         * @type {String}
         */
        url = '';

        /**
         * @description 服务类型 (WMS/WMTS)
         * @type {EnumOGCType}
         * @readonly
         */
        serviceType = '';

        /**
         * @description 服务版本
         * @type {String}
         */
        version = '';

        /**
         * @description 创建OGC服务连接器
         * @param {Object} props 包含如下属性的Object：
         * @param {String} props.url 服务地址
         * @param {EnumOGCType} props.serviceType 服务类型
         * @param {String} props.version 服务版本
         */
        constructor(props) {
            if (!defined(props) || !defined(props.url)) {
                throw new Error('OGCConnector requires a url');
            }

            this.url = props.url;
            this.serviceType = props.serviceType || '';
            this.version = props.version || '';
        }

        /**
         * 获取服务能力信息
         * @param {Function} callback 回调函数，参数为(error, capabilities)
         * @abstract
         */
        getCapabilities(callback) {
            throw new Error('方法必须由子类实现');
        }

        /**
         * 获取图层名称列表
         * @param {Function} callback 回调函数，参数为(error, layerNames)
         */
        getLayers(callback) {
            this.getCapabilities((error, capabilities) => {
                if (error) {
                    if (defined(callback)) {
                        callback(error, null);
                    }
                    return;
                }

                // 获取图层详细信息
                const layerInfos = this._extractLayers(capabilities);

                // 提取图层名称
                const layerNames = layerInfos.map(layer => layer.layerName || layer.name);

                if (defined(callback)) {
                    callback(null, layerNames);
                }
            });
        }

        /**
         * 从能力文档中提取图层信息
         * @param {Object} capabilities 能力文档对象
         * @returns {Array} 图层信息数组
         * @protected
         * @abstract
         */
        _extractLayers(capabilities) {
            throw new Error('方法必须由子类实现');
        }

        /**
         * 获取图层详细信息
         * @param {String} layerName 图层名称
         * @param {Function} callback 回调函数，参数为(error, layerInfo)
         */
        getLayerInfo(layerName, callback) {
            this.getLayersInfo((error, layerInfos) => {
                if (error) {
                    if (defined(callback)) {
                        callback(error, null);
                    }
                    return;
                }
                
                // 查找指定图层
                const layer = this._findLayerByName(layerInfos, layerName);
                if (layer) {
                    if (defined(callback)) {
                        callback(null, layer);
                    }
                } else {
                    if (defined(callback)) {
                        callback(new Error(`图层 "${layerName}" 未找到`), null);
                    }
                }
            });
        }

        /**
         * 获取完整的图层信息对象列表
         * @param {Function} callback 回调函数，参数为(error, layerInfos)
         */
        getLayersInfo(callback) {
            this.getCapabilities((error, capabilities) => {
                if (error) {
                    if (defined(callback)) {
                        callback(error, null);
                    }
                    return;
                }

                // 获取完整的图层信息
                const layerInfos = this._extractLayers(capabilities);

                if (defined(callback)) {
                    callback(null, layerInfos);
                }
            });
        }
        /**
         * 在图层列表中查找指定名称的图层
         * @param {Array} layers 图层列表
         * @param {String} layerName 图层名称
         * @returns {Object|null} 图层信息对象
         * @protected
         */
        _findLayerByName(layers, layerName) {
            for (let i = 0; i < layers.length; i++) {
                if (layers[i].layerName === layerName) {
                    return layers[i];
                }
            }
            return null;
        }

        /**
         * 构建请求URL，附加服务类型和版本参数
         * @param {Object} params 请求参数
         * @returns {String} 完整的请求URL
         * @protected
         */
        _buildUrl(params) {
            let url = this.url;
            // 确保URL包含?号
            if (url.indexOf('?') === -1) {
                url += '?';
            } else if (!url.endsWith('?') && !url.endsWith('&')) {
                url += '&';
            }

            // 添加服务类型和版本参数
            let query = `SERVICE=${this.serviceType}&VERSION=${this.version}`;

            // 添加其他参数
            for (const key in params) {
                if (params.hasOwnProperty(key)) {
                    query += `&${key}=${params[key]}`;
                }
            }

            return url + query;
        }

        /**
         * 执行HTTP请求
         * @param {String} url 请求URL
         * @param {Function} callback 回调函数，参数为(error, response)
         * @protected
         */
        _request(url, callback) {
            fetch(url)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }
                    return response.text();
                })
                .then(text => {
                    if (defined(callback)) {
                        callback(null, text);
                    }
                })
                .catch(error => {
                    console.error("请求失败:", error);
                    if (defined(callback)) {
                        callback(error, null);
                    }
                });
        }

        /**
         * 解析XML文本
         * @param {String} text XML文本
         * @returns {Document} XML文档对象
         * @protected
         */
        _parseXML(text) {
            const parser = new DOMParser();
            return parser.parseFromString(text, "text/xml");
        }

        /**
         * 获取XML节点中指定元素的文本内容
         * @param {Element} node 父节点
         * @param {String} tagName 子节点标签名
         * @returns {String|null} 文本内容
         * @protected
         */
        _getElementText(node, tagName) {
            const elements = node.getElementsByTagName(tagName);
            if (elements.length > 0) {
                return elements[0].textContent;
            }
            return null;
        }
    }

    return OGCConnector;
});